import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { StoriesBar } from './StoriesBar';
import { Eye, ThumbsUp, MessageCircle, Share2, Bookmark, Clock, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryColors = {
    'blog': 'bg-primary text-primary-foreground',
    'news': 'bg-accent-green text-primary-foreground',
    'guide': 'bg-accent-yellow text-foreground',
    'story': 'bg-secondary text-secondary-foreground',
    'Poradnik': 'bg-accent-green text-primary-foreground',
    'Edukacja': 'bg-primary text-primary-foreground',
    'Rozrywka': 'bg-accent-yellow text-foreground',
    'Nowości': 'bg-accent-green text-primary-foreground',
    'Historia': 'bg-secondary text-secondary-foreground',
};

const categoryNames = {
    'blog': 'Blog',
    'news': 'Nowości',
    'guide': 'Poradnik',
    'story': 'Historia'
};

export const NewsFeed = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [likedArticles, setLikedArticles] = useState({});
    const [savedArticles, setSavedArticles] = useState({});

    useEffect(() => {
        const fetchArticles = async () => {
            try {
                const response = await fetch(`${API_URL}/api/articles?limit=5`);
                if (response.ok) {
                    const data = await response.json();
                    setArticles(data.articles || []);
                }
            } catch (error) {
                console.error('Error fetching articles:', error);
            }
            setLoading(false);
        };
        fetchArticles();
    }, []);

    const handleLike = (articleId) => {
        setLikedArticles(prev => ({
            ...prev,
            [articleId]: !prev[articleId]
        }));
    };

    const handleSave = (articleId) => {
        setSavedArticles(prev => ({
            ...prev,
            [articleId]: !prev[articleId]
        }));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return 'Dzisiaj';
        if (diffDays === 1) return 'Wczoraj';
        if (diffDays < 7) return `${diffDays} dni temu`;
        return date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' });
    };

    return (
        <main className="space-y-4">
            {/* Stories Section - Modern touch */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header text-sm">
                    Szkolne relacje
                </div>
                <div className="p-3">
                    <StoriesBar />
                </div>
            </div>

            {/* News Feed Header */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header flex items-center justify-between">
                    <h2 className="text-sm font-semibold m-0">Aktualności z redakcji</h2>
                    <Link to="/blog">
                        <Badge className="nk-badge-yellow text-[10px] cursor-pointer hover:opacity-80">
                            {articles.length > 0 ? `${articles.length} artykułów` : 'Zobacz wszystkie'}
                        </Badge>
                    </Link>
                </div>

                {loading ? (
                    <div className="p-8 text-center">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" />
                    </div>
                ) : articles.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                        <p>Brak opublikowanych artykułów</p>
                        <Link to="/blog" className="text-primary hover:underline text-sm mt-2 inline-block">
                            Sprawdź blog →
                        </Link>
                    </div>
                ) : (
                <div className="divide-y divide-border">
                    {articles.map((article, index) => (
                        <Link 
                            to={`/blog/${article.slug}`}
                            key={article.id} 
                            className="block p-4 hover:bg-muted/50 transition-colors cursor-pointer group animate-fade-in-up"
                            style={{ animationDelay: `${index * 100}ms` }}
                        >
                            <div className="flex gap-4">
                                {/* Article Image */}
                                <div className="flex-shrink-0">
                                    <div className="nk-avatar w-24 h-20 sm:w-32 sm:h-24 rounded-lg overflow-hidden group-hover:shadow-nk-glow transition-shadow bg-muted">
                                        {article.featured_image ? (
                                            <picture>
                                                {article.featured_image_thumb && (
                                                    <source
                                                        srcSet={article.featured_image_thumb.startsWith('http') ? article.featured_image_thumb : `${API_URL}${article.featured_image_thumb}`}
                                                        type="image/webp"
                                                    />
                                                )}
                                                <img
                                                    src={article.featured_image.startsWith('http') ? article.featured_image : `${API_URL}${article.featured_image}`}
                                                    alt={article.title}
                                                    width="400"
                                                    height="250"
                                                    loading="lazy"
                                                    decoding="async"
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                            </picture>
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-2xl">📰</div>
                                        )}
                                    </div>
                                </div>

                                {/* Article Content */}
                                <div className="flex-1 min-w-0 flex flex-col">
                                    {/* Category & Date */}
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Badge className={`text-[10px] px-2 py-0 ${categoryColors[article.category] || 'bg-muted'}`}>
                                            {categoryNames[article.category] || article.category}
                                        </Badge>
                                        <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                            <Clock className="w-3 h-3" />
                                            {formatDate(article.publish_at || article.created_at)}
                                        </span>
                                    </div>

                                    {/* Title - h3 bo jest w kartcie po Aktualnosciach h2. NewsFeed sekcja glowna ma h2 wyzej. */}
                                    <h3 className="font-semibold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-1">
                                        {article.title}
                                    </h3>

                                    {/* Excerpt */}
                                    <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 flex-1">
                                        {article.excerpt || article.meta_description}
                                    </p>

                                    {/* Stats & Actions */}
                                    <div className="flex items-center justify-between mt-2 pt-2 border-t border-border/50">
                                        {/* Stats */}
                                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                            <span className="flex items-center gap-1">
                                                <Eye className="w-3.5 h-3.5" />
                                                {article.views || 0}
                                            </span>
                                            <span className="flex items-center gap-1">
                                                <ThumbsUp className="w-3.5 h-3.5" />
                                                {(article.rating_count || 0) + (likedArticles[article.id] ? 1 : 0)}
                                            </span>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLike(article.id); }}
                                                className={`h-7 px-2 ${likedArticles[article.id] ? 'text-accent-green' : 'text-muted-foreground hover:text-accent-green'}`}
                                            >
                                                <ThumbsUp className={`w-3.5 h-3.5 ${likedArticles[article.id] ? 'fill-current' : ''}`} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-muted-foreground hover:text-primary"
                                            >
                                                <MessageCircle className="w-3.5 h-3.5" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={(e) => { e.stopPropagation(); handleSave(article.id); }}
                                                className={`h-7 px-2 ${savedArticles[article.id] ? 'text-accent-yellow' : 'text-muted-foreground hover:text-accent-yellow'}`}
                                            >
                                                <Bookmark className={`w-3.5 h-3.5 ${savedArticles[article.id] ? 'fill-current' : ''}`} />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 px-2 text-muted-foreground hover:text-primary"
                                            >
                                                <Share2 className="w-3.5 h-3.5" />
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
                )}

                {/* Load more button */}
                {articles.length > 0 && (
                <div className="p-4 border-t border-border">
                    <Link to="/blog">
                        <Button className="w-full nk-button">
                            Zobacz wszystkie artykuły
                        </Button>
                    </Link>
                </div>
                )}
            </div>
        </main>
    );
};
